import {mapToDataCloudEvents} from './mappers'
import type {
  DataCloudDispatch,
  GetDataCloudContext,
  MapOptions,
  SalesforceInteractionsEvent,
  SalesforceInteractionsSDK,
  WhenBasketSettled
} from './types'

const IDENTITY_SESSION_KEY = 'dataCloudPartyId'

const getIdentityUserId = (events: SalesforceInteractionsEvent[]) => {
  const userId = events.find((event) => event.user?.attributes?.userId)?.user?.attributes.userId
  return typeof userId === 'string' ? userId : null
}

const isDuplicateIdentity = (userId: string | null) => {
  if (!userId) return false
  try {
    return window.sessionStorage.getItem(IDENTITY_SESSION_KEY) === userId
  } catch {
    return false
  }
}

const rememberIdentity = (userId: string | null) => {
  if (!userId) return
  try {
    window.sessionStorage.setItem(IDENTITY_SESSION_KEY, userId)
  } catch {
    // sessionStorage can be unavailable (e.g. Safari private mode); a repeat send is harmless
  }
}

export interface DataCloudDispatcher extends DataCloudDispatch {
  resendPendingIdentity: () => void
}

const MAX_BUFFERED_EVENTS = 50

const isCartEvent = (event: SalesforceInteractionsEvent) =>
  Boolean(event.interaction?.lineItem || event.interaction?.lineItems)

/**
 * Holds events fired before Data Cloud has started (e.g. userData on first load),
 * then replays them once a dispatcher is attached.
 */
export const createBufferedDispatch = () => {
  let buffer: unknown[] = []
  let target: DataCloudDispatch | null = null
  let discarded = false

  const dispatch: DataCloudDispatch = (data) => {
    if (target) return target(data)
    if (discarded || buffer.length >= MAX_BUFFERED_EVENTS) return
    try {
      buffer.push(JSON.parse(JSON.stringify(data)))
    } catch {
      // Unserialisable payloads can't be mapped anyway
    }
  }

  return {
    dispatch,
    attach(real: DataCloudDispatch) {
      target = real
      discarded = false
      const pending = buffer
      buffer = []
      pending.forEach((data) => real(data))
    },
    detach() {
      target = null
    },
    discard() {
      target = null
      discarded = true
      buffer = []
    }
  }
}

export const createDataCloudDispatcher = (
  sdkReady: Promise<SalesforceInteractionsSDK>,
  options: MapOptions = {},
  isOptedIn: () => boolean = () => true,
  getContext: GetDataCloudContext = () => ({}),
  whenBasketSettled: WhenBasketSettled = () => Promise.resolve()
): DataCloudDispatcher => {
  // The SDK silently drops events without consent, so profile events are held until Opt In
  let pendingIdentity: SalesforceInteractionsEvent[] | null = null

  const sendNow = (sdk: SalesforceInteractionsSDK, event: SalesforceInteractionsEvent) => {
    try {
      sdk.sendEvent(event)
      return true
    } catch (error) {
      console.warn('Data Cloud sendEvent failed', error)
      return false
    }
  }

  // Profile events from one login (customer number and email) are held and deduped together
  const sendIdentity = (events: SalesforceInteractionsEvent[]) => {
    const userId = getIdentityUserId(events)
    if (isDuplicateIdentity(userId)) return
    if (!isOptedIn()) {
      pendingIdentity = events
      return
    }

    sdkReady.then(
      (sdk) => {
        const sent = events.map((event) => sendNow(sdk, event))
        if (sent.every(Boolean)) rememberIdentity(userId)
      },
      () => undefined
    )
  }

  const send = (event: SalesforceInteractionsEvent) => {
    // Cart tracking fires on click, before the basket update is saved
    const ready = isCartEvent(event)
      ? Promise.all([sdkReady, whenBasketSettled().catch(() => undefined)]).then(([sdk]) => sdk)
      : sdkReady

    ready.then(
      (sdk) => {
        sendNow(sdk, event)
      },
      () => undefined
    )
  }

  const dispatch = (data: unknown) => {
    try {
      // Deep copy so nothing here can mutate the object already pushed to the GTM dataLayer
      const payload = JSON.parse(JSON.stringify(data))
      const {basketId, email} = getContext()
      const events = mapToDataCloudEvents(payload, {...options, basketId, email})
      const profileEvents = events.filter((event) => event.user)
      if (profileEvents.length) sendIdentity(profileEvents)
      events.filter((event) => !event.user).forEach(send)
    } catch (error) {
      console.warn('Data Cloud dispatch failed', error)
    }
  }

  return Object.assign(dispatch, {
    resendPendingIdentity: () => {
      const events = pendingIdentity
      pendingIdentity = null
      if (events) sendIdentity(events)
    }
  })
}
